import { ButtonBase, DialogActions } from '@material-ui/core';
import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef
} from 'react';
import { useTranslation } from 'react-i18next';

import moment from 'moment';

import * as Yup from 'yup';
import {
  AutocompleteComponent,
  DatePickerComponent,
  DialogComponent,
  Inputs,
} from '../../../../Components';
import {
  getDownloadableLink,
  showError,
  showSuccess,
} from '../../../../Helper';
import { CreateAward } from '../../../../Services/AwardServices';
import { ApplicationUserSearch } from '../../../../Services/userServices';
import { MultipleTypesDocumentsUploader } from '../../../../SharedComponents';

function CreateNewAwardDialog({
  isOpen,
  setIsCreateDialogOpen,
  translationPath,
  refetchData,
}) {
  const { t } = useTranslation(translationPath);
  const reducer = useCallback((state, action) => {
    if (action.id !== 'edit') return { ...state, [action.id]: action.value };
    return {
      ...action.value,
    };
  }, []);
  const searchTimer = useRef(null);
  const [state, setState] = useReducer(reducer, {
    userData: null,
    awardName: null,
    awardDate: null,
    awardDocument: [],
    supportingDocuments: [],
  });
  const [data, setData] = useReducer(reducer, {
    users: null,
  });

  const unitValidationSchema = Yup.object().shape({
    userData: Yup.object().required(t('please-select-user')),
    awardName: Yup.string().required(t('award-name-is-required')),
    awardDate: Yup.date()
      .default(() => new Date())
      .required(t('award-date-is-required')),
  });

  async function GetAllUsers(searchValue) {
    const res = await ApplicationUserSearch({
      name: searchValue || '',
      pageIndex: 1,
      pageSize: 50,
    });

    if (!(res && res.status && res.status !== 200)) {
      setData({ id: 'users', value: res });
    } else {
      setData([]);
      showError(t('error-fetching-users'));
    }
  }

  async function onCreateClicked() {
    try {
      await unitValidationSchema.validate(state, { abortEarly: false });
      const body = {
        awardName: state.awardName,
        awardDate: state.awardDate,
        usersId: state.userData.applicationUserId,
        awardDocuments: [...state.awardDocument, ...state.supportingDocuments],
      };
      const res = await CreateAward(body);
      if (!(res && res.status && res.status !== 200)) {
        await refetchData();
        showSuccess(t('award-created-successfully'));
        setIsCreateDialogOpen(false);
      } else {
        showError(t('award-creation-failed'));
      }
    } catch (error) {
      showError(t('please-fill-all-required-fields'));
    } finally {
    }
  }

  useEffect(() => {
    GetAllUsers();
  }, []);

  return (
    <DialogComponent
      isOpen={isOpen}
      titleText={t(`create-new-award`)}
      maxWidth='sm'
      dialogContent={
        <>
          <div className='create-user-award-dialog-wrapper'>
            <div className='select-user-section-container'>
              <AutocompleteComponent
                idRef='selectUserForAwardRef'
                labelValue={t('select-user')}
                labelClasses='Requierd-Color'
                selectedValues={state?.userData || null}
                data={data?.users?.result || []}
                displayLabel={(option) => option?.fullName || ''}
                renderOption={(option) => option?.fullName || ''}
                withoutSearchButton
                multiple={false}
                parentTranslationPath={translationPath}
                translationPath={''}
                onChange={(e, newValue) => {
                  setState({ id: 'userData', value: newValue || null });
                }}
                onInputKeyUp={(e) => {
                  const value = e.target.value || null;
                  if (searchTimer) clearTimeout(searchTimer.current);
                  searchTimer.current = setTimeout(() => {
                    GetAllUsers(value);
                  }, 1000);
                }}
              />
            </div>

            <Inputs
              idRef={'awardNameRef'}
              labelValue={'award-name'}
              type={'text'}
              labelClasses='Requierd-Color'
              value={state.awardName || ''}
              onInputChanged={(e) => {
                const value = e.target.value ?? null;
                setState({ id: 'awardName', value: value });
              }}
              translationPath={''}
              parentTranslationPath={translationPath}
            />

            <DatePickerComponent
              type='date'
              labelValue='award-date'
              value={state.awardDate || null}
              labelClasses='Requierd-Color'
              onDateChanged={(value) => {
                const newValue = !value
                  ? null
                  : moment(value).format('YYYY-MM-DDTHH:mm:ss');

                setState({ id: 'awardDate', value: newValue });
              }}
              translationPath={''}
              parentTranslationPath={translationPath}
            />

            <MultipleTypesDocumentsUploader
              idRef='awardCertificateUploaderRef'
              initUploadedFiles={state?.awardDocument || []}
              labelValue='award-certificate'
              accept='application/pdf, image/png, image/jpg'
              uploadedChanged={(files) => {
                const newFiles = files.map((item) => {
                  return {
                    documentName: item.fileName,
                    fileId: item.uuid,
                    documentLink: item.url,
                    isCertificate: true,
                  };
                });

                setState({
                  id: 'awardDocument',
                  value: newFiles,
                });
              }}
              multiple={false}
              isOpenGallery
              viewUploadedFilesCount={false}
              openGallery
              isDocuments
              chipHandler={(value) => () => {
                const link = document.createElement('a');
                link.setAttribute('download', value.fileName);
                link.href = getDownloadableLink(value.uuid);
                document.body.appendChild(link);
                link.click();
                link.remove();
              }}
              translationPath={''}
              parentTranslationPath={translationPath}
            />
            <MultipleTypesDocumentsUploader
              idRef='awardCertificateUploaderRef'
              initUploadedFiles={state?.supportingDocuments || []}
              labelValue='supporting-documents'
              uploadedChanged={(files) => {
                const newFiles = files.map((item) => {
                  return {
                    documentName: item.fileName,
                    fileId: item.uuid,
                    documentLink: item.url,
                    isCertificate: false,
                  };
                });

                setState({
                  id: 'supportingDocuments',
                  value: newFiles,
                });
              }}
              multiple={true}
              isOpenGallery
              viewUploadedFilesCount={false}
              openGallery
              isDocuments
              chipHandler={(value) => () => {
                const link = document.createElement('a');
                link.setAttribute('download', value.fileName);
                link.href = getDownloadableLink(value.uuid);
                document.body.appendChild(link);
                link.click();
                link.remove();
              }}
              translationPath={''}
              parentTranslationPath={translationPath}
            />
          </div>

          <DialogActions>
            <ButtonBase
              onClick={() => {
                setIsCreateDialogOpen(false);
              }}
              className={`btns theme-solid bg-cancel`}
            >
              {t(`cancel`)}
            </ButtonBase>
            <ButtonBase
              className={`btns theme-solid`}
              onClick={() => {
                onCreateClicked();
              }}
            >
              {t(`create`)}
            </ButtonBase>
          </DialogActions>
        </>
      }
      translationPath={''}
      parentTranslationPath={translationPath}
    />
  );
}

export default CreateNewAwardDialog;
